/**
 * "Sequence wise" statuses (`Status.isSequential` — the toggle on the stage
 * editor's status header): the status's sub-statuses are walked in their
 * configured order, one step at a time.
 *
 *  - Entering the status (the draft status is not the one the lead/deal sits
 *    in now) → only its FIRST sub-status is offered.
 *  - Within the status → every sub-status up to and including the ONE right
 *    after the current one. Earlier steps stay pickable so a mis-click can be
 *    corrected; later steps unlock as the lead advances (at Attempt 2,
 *    Attempt 3 is offered and Attempt 4+ is not).
 *
 * Steps are ranked by `order`, tie-broken on `id` — the server's read order
 * (legacy rows carry order 0) — never by array index. A pure subtraction
 * applied after the caller's own filters; `pinnedIds` (the server value and
 * the draft pick) always survive so the trigger never renders empty.
 *
 * UI-only, like the next-stage-only provision: automations, imports, bulk
 * update and the API may still set any sub-status. It applies to admins too —
 * it describes the process, not a privilege.
 *
 * Mirrored in the mobile app (Xale_App) — keep the two in step.
 */
type Id = string | number;

export interface SequentialSubStatusLike {
  id: Id;
  order?: number | null;
}

export function filterSequentialSubStatuses<T extends SequentialSubStatusLike>(
  subStatuses: T[] | null | undefined,
  isSequential: boolean | null | undefined,
  opts: {
    /** True when the draft status differs from the entity's current one. */
    entering: boolean;
    /** The entity's current (server) sub-status. */
    currentSubStatusId?: Id | null;
    /** Ids that must stay listed regardless (server value, draft pick). */
    pinnedIds?: Array<Id | null | undefined>;
  },
): T[] {
  const subs = subStatuses ?? [];
  if (!isSequential || subs.length <= 1) return subs;

  const ranked = [...subs].sort(
    (a, b) =>
      (Number(a.order) || 0) - (Number(b.order) || 0) ||
      Number(a.id) - Number(b.id),
  );
  const currentIdx = opts.entering
    ? -1
    : ranked.findIndex((s) => String(s.id) === String(opts.currentSubStatusId));
  // Not positioned in this status (entering it, or an id we can't find) →
  // the first step only. Otherwise up to one step past the current one.
  const lastAllowed = currentIdx === -1 ? 0 : currentIdx + 1;

  const allowed = new Set(
    ranked.slice(0, lastAllowed + 1).map((s) => String(s.id)),
  );
  for (const id of opts.pinnedIds ?? []) {
    if (id != null && id !== "") allowed.add(String(id));
  }
  return subs.filter((s) => allowed.has(String(s.id)));
}
